/**
 * Suppression Rules Section for the Settings page.
 *
 * Table-based management view for viewing, toggling, editing, and deleting
 * per-organization suppression rules.
 */

import { useState, useEffect } from "react";
import {
  BellOff,
  Pencil,
  Trash2,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Filter,
  ShieldOff,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSuppressionRules, useUpdateSuppressionRule } from "@/hooks/useSuppressionRules";
import { EditSuppressionRuleDialog } from "./EditSuppressionRuleDialog";
import { DeleteSuppressionRuleDialog } from "./DeleteSuppressionRuleDialog";
import type { SuppressionRule } from "@/types/suppressionRule";

/**
 * Per-row component encapsulating its own `useUpdateSuppressionRule` mutation.
 * This avoids shared-mutation race conditions when toggling multiple rows rapidly.
 */
function SuppressionRuleRow({
  rule,
  onEdit,
  onDelete,
}: {
  rule: SuppressionRule;
  onEdit: (rule: SuppressionRule) => void;
  onDelete: (rule: SuppressionRule) => void;
}) {
  const updateMutation = useUpdateSuppressionRule();

  const isExpired = rule.expires_at && new Date(rule.expires_at) < new Date();

  // Derived state pattern: show optimistic value while mutation is in flight
  const isActive = updateMutation.isPending
    ? updateMutation.variables!.data.is_active!
    : rule.is_active;

  function handleToggle(checked: boolean) {
    updateMutation.mutate({ ruleId: rule.id, data: { is_active: checked } });
  }

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  // Build criteria badges
  const criteria: { label: string; value: string }[] = [];
  if (rule.match_issue_type) criteria.push({ label: "Type", value: rule.match_issue_type });
  if (rule.match_severity) criteria.push({ label: "Severity", value: rule.match_severity });
  if (rule.match_scanner) criteria.push({ label: "Scanner", value: rule.match_scanner });
  if (rule.match_title_pattern) criteria.push({ label: "Title", value: rule.match_title_pattern });

  return (
    <TableRow className={!isActive ? "opacity-60" : undefined}>
      {/* Name & Description */}
      <TableCell>
        <div>
          <p className="font-medium text-sm">{rule.name}</p>
          {rule.description && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
              {rule.description}
            </p>
          )}
        </div>
      </TableCell>

      {/* Match Criteria */}
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {criteria.map((c) => (
            <Badge
              key={c.label}
              variant="outline"
              className="text-[11px] font-normal px-1.5 py-0"
            >
              <span className="text-muted-foreground mr-1">{c.label}:</span>
              <span className="font-medium truncate max-w-[120px]">{c.value}</span>
            </Badge>
          ))}
          {criteria.length === 0 && (
            <span className="text-xs text-muted-foreground italic">None</span>
          )}
        </div>
      </TableCell>

      {/* Status Toggle */}
      <TableCell>
        <Switch
          checked={isActive}
          onCheckedChange={handleToggle}
          disabled={updateMutation.isPending}
          aria-label={`Toggle rule ${rule.name}`}
        />
      </TableCell>

      {/* Expires */}
      <TableCell>
        {rule.expires_at ? (
          isExpired ? (
            <Badge variant="destructive" className="text-[11px] font-normal">
              Expired
            </Badge>
          ) : (
            <span className="text-xs text-muted-foreground">
              {formatDate(rule.expires_at)}
            </span>
          )
        ) : (
          <span className="text-xs text-muted-foreground">Never</span>
        )}
      </TableCell>

      {/* Created By */}
      <TableCell>
        <span className="text-xs text-muted-foreground">
          {rule.user_name || rule.user_email || "System"}
        </span>
      </TableCell>

      {/* Actions */}
      <TableCell>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
            onClick={() => onEdit(rule)}
            title="Edit rule"
            aria-label={`Edit rule ${rule.name}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
            onClick={() => onDelete(rule)}
            title="Delete rule"
            aria-label={`Delete rule ${rule.name}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export function SuppressionRulesSection() {
  const [page, setPage] = useState(1);
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [editRule, setEditRule] = useState<SuppressionRule | null>(null);
  const [deleteRule, setDeleteRule] = useState<SuppressionRule | null>(null);

  const isActiveParam = activeFilter === "active" ? true : activeFilter === "inactive" ? false : undefined;
  const { data, isLoading } = useSuppressionRules(page, isActiveParam);

  const rules = data?.data ?? [];
  const totalCount = data?.total_count ?? 0;
  const itemsPerPage = data?.items_per_page ?? 20;
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  useEffect(() => {
    if (page > totalPages) setPage(Math.max(1, totalPages));
  }, [totalPages, page]);

  // Reset page when filter changes
  function handleFilterChange(value: string) {
    setActiveFilter(value);
    setPage(1);
  }

  return (
    <div className="space-y-6">
      <Card className="shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BellOff className="h-5 w-5" />
                Suppression Rules
              </CardTitle>
              <CardDescription>
                Rules that prevent future security issues matching specific criteria from being created
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={activeFilter} onValueChange={handleFilterChange}>
                <SelectTrigger className="w-[130px] h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Rules</SelectItem>
                  <SelectItem value="active">Active Only</SelectItem>
                  <SelectItem value="inactive">Inactive Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : rules.length === 0 ? (
            /* Empty State */
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <ShieldOff className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-sm font-medium mb-1">No suppression rules</h3>
              <p className="text-xs text-muted-foreground max-w-sm">
                {activeFilter !== "all"
                  ? `No ${activeFilter} rules found. Try changing the filter.`
                  : "Suppression rules prevent future issues matching specific criteria. Create one from the issue detail page using the Suppress action."}
              </p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[220px]">Name</TableHead>
                    <TableHead>Match Criteria</TableHead>
                    <TableHead className="w-[80px]">Status</TableHead>
                    <TableHead className="w-[100px]">Expires</TableHead>
                    <TableHead className="w-[120px]">Created By</TableHead>
                    <TableHead className="w-[80px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rules.map((rule) => (
                    <SuppressionRuleRow
                      key={rule.id}
                      rule={rule}
                      onEdit={setEditRule}
                      onDelete={setDeleteRule}
                    />
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalCount > itemsPerPage && (
                <div className="flex items-center justify-between border-t pt-4 mt-4">
                  <p className="text-xs text-muted-foreground">
                    Showing {(page - 1) * itemsPerPage + 1}–{Math.min(page * itemsPerPage, totalCount)} of {totalCount} rules
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <span className="text-xs text-muted-foreground px-2">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      {editRule && (
        <EditSuppressionRuleDialog
          open={!!editRule}
          onOpenChange={(open) => !open && setEditRule(null)}
          rule={editRule}
        />
      )}

      {/* Delete Dialog */}
      {deleteRule && (
        <DeleteSuppressionRuleDialog
          open={!!deleteRule}
          onOpenChange={(open) => !open && setDeleteRule(null)}
          rule={deleteRule}
        />
      )}
    </div>
  );
}
